import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Calendar, Clock, MapPin, Trophy, Ticket, Store, Gamepad2, ExternalLink, Share2 } from "lucide-react";
import { MarkdownBody } from "./NewsDetailPage";
import { formatDate } from "./NewsPage";
import { TorneoRegistration } from "../components/torneos/TorneoRegistration";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const Info = ({ icon: Icon, label, value, testid }) => value ? (
  <div className="flex items-start gap-3 p-3 rounded-xl bg-purple-900/30 border border-purple-800/40" data-testid={testid}>
    <Icon size={18} className="text-yellow-400 flex-shrink-0 mt-0.5" />
    <div className="min-w-0"><p className="text-[11px] uppercase tracking-wider text-gray-400">{label}</p><p className="text-sm font-semibold text-white break-words">{value}</p></div>
  </div>
) : null;

export default function TorneoDetailPage({ user }) {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => { axios.get(`${API}/torneos/${id}`).then((r) => setT(r.data)).catch(() => setError(true)); }, [id]);

  if (error) return <div className="min-h-screen bg-black pt-32 text-center text-gray-400" data-testid="torneo-not-found">Torneo no encontrado. <Link to="/torneos" className="text-pink-400">Volver</Link></div>;
  if (!t) return <div className="min-h-screen bg-black pt-32 flex justify-center"><div className="w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full animate-spin" /></div>;

  const share = () => {
    const url = window.location.href;
    if (navigator.share) navigator.share({ title: t.name, url }).catch(() => {});
    else window.open(`https://wa.me/?text=${encodeURIComponent(t.name + " " + url)}`, "_blank");
  };

  return (
    <div className="min-h-screen bg-black pt-24 pb-16 overflow-x-hidden" data-testid="torneo-detail-page">
      <div className="container mx-auto px-4">
        <Link to="/torneos" className="inline-flex items-center gap-2 text-pink-400 text-sm mb-6 hover:text-pink-300"><ArrowLeft size={16} /> Todos los torneos</Link>
        <div className="grid lg:grid-cols-[380px_1fr] gap-10 min-w-0">
          <div className="relative rounded-2xl overflow-hidden border border-purple-500/30 shadow-[0_0_60px_rgba(168,85,247,0.25)] self-start">
            {t.image ? <img src={t.image} alt={t.name} className="w-full object-cover" data-testid="torneo-poster" /> : <div className="aspect-[3/4] bg-gradient-to-br from-purple-900 to-black flex items-center justify-center"><Trophy size={64} className="text-yellow-400/40" /></div>}
            <span className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-black ${t.status === "proximo" ? "bg-emerald-400 text-black" : "bg-gray-800 text-gray-200"}`}>{t.status === "proximo" ? "PRÓXIMO" : "FINALIZADO"}</span>
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap gap-2 mb-3">
              {t.format && <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-purple-600/40 text-purple-200 border border-purple-500/40">{t.format}</span>}
              <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-pink-600/30 text-pink-200 border border-pink-500/40">{t.juego}</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight mb-6 break-words" style={{ fontFamily: "Cinzel, serif" }} data-testid="torneo-name">{t.name}</h1>

            <div className="grid sm:grid-cols-2 gap-3 mb-6">
              <Info icon={Calendar} label="Fecha" value={formatDate(t.date)} testid="torneo-date" />
              <Info icon={Clock} label="Hora" value={t.time} testid="torneo-time" />
              <Info icon={MapPin} label="Ubicación" value={t.location} testid="torneo-location" />
              <Info icon={Ticket} label="Inscripción" value={t.entry_fee} testid="torneo-fee" />
              <Info icon={Trophy} label="Premios" value={t.prizes} testid="torneo-prizes" />
              <Info icon={Gamepad2} label="Campeón" value={t.winner ? <Link to={`/jugador/${encodeURIComponent(t.winner)}`} className="hover:text-yellow-300 underline decoration-yellow-400/40">{t.winner}</Link> : null} testid="torneo-winner" />
            </div>

            <TorneoRegistration torneo={t} user={user} />

            <div className="flex flex-wrap gap-3 mb-8">
              {t.registration_url && t.status === "proximo" && <a href={t.registration_url} target="_blank" rel="noreferrer" className="px-5 py-3 rounded-xl border border-yellow-400/50 text-yellow-300 font-semibold hover:bg-yellow-400/10 transition flex items-center gap-2" data-testid="torneo-external-register">Inscripción externa <ExternalLink size={14} /></a>}
              <button onClick={share} className="px-5 py-3 rounded-xl border border-purple-500/50 text-purple-200 font-semibold hover:bg-purple-500/20 transition flex items-center gap-2" data-testid="torneo-share-btn"><Share2 size={16} /> Compartir</button>
            </div>

            {t.tienda && (
              <div className="flex items-center gap-4 p-4 rounded-2xl glass-card border border-purple-500/30 hover:border-yellow-400/50 transition mb-8" data-testid="torneo-store-card">
                <Link to="/tiendas" className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="w-14 h-14 rounded-xl bg-purple-900/60 flex items-center justify-center text-yellow-400 overflow-hidden flex-shrink-0 p-1">{t.tienda.logo_url ? <img src={t.tienda.logo_url} alt="" className="w-full h-full object-contain" /> : <Store size={26} />}</div>
                  <div className="min-w-0"><p className="text-[11px] tracking-widest text-gray-400">SEDE · {t.tienda.estatus}</p><p className="font-bold text-white">{t.tienda.nombre}</p><p className="text-xs text-gray-400 truncate">{t.tienda.direccion_texto}</p></div>
                </Link>
                <a href={t.tienda.maps_url} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-lg bg-yellow-400 text-black text-xs font-bold flex items-center gap-1 flex-shrink-0"><MapPin size={13} /> Maps</a>
              </div>
            )}

            {t.description && <div className="glass-card rounded-2xl p-6 border border-purple-500/20"><MarkdownBody>{t.description}</MarkdownBody></div>}

            {t.gallery?.length > 0 && (
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="torneo-gallery">
                {t.gallery.map((g, i) => <img key={i} src={g} alt="" className="rounded-xl object-cover aspect-square border border-purple-800/40" />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
